import { z } from 'zod'

import { paginacaoSearchFields, toLimitOffset } from '@/widgets/data-table/table-search'

import type { BankTxDirection, BankTxListParams, BankTxStatus } from './api'

/**
 * Search params das 4 telas de contas (a pagar/receber/pagas/recebidas).
 *
 * O `bank-transactions` aceita bem menos filtros que os agendamentos: só
 * PERÍODO (por vencimento). `direction`/`status` são a identidade da tela (não
 * viram query param editável), e valor/busca/contato/categoria/centro não
 * existem no endpoint — filtro que o endpoint não suporta viraria filtragem só
 * da página atual, silenciosamente errada.
 */
export const bankTxSearchSchema = z.object({
  ...paginacaoSearchFields,
  de: z.iso.date().optional(),
  ate: z.iso.date().optional(),
  /**
   * Situação (status) — filtro OPCIONAL de "Mais opções de filtro".
   *
   * `direction`/`status` são a IDENTIDADE da tela; `situacao` só NARRA dentro
   * dela (ex.: "a pagar" mostra open+overdue+partial, e o usuário pode reduzir
   * a só "overdue"). Fora da identidade da tela é ignorado (ver
   * `toBankTxListParams`), então nunca vaza um settled para a tela de aberto.
   */
  situacao: z.enum(['open', 'overdue', 'partial', 'settled']).optional(),
  ordenar: z
    .enum([
      'due_date',
      '-due_date',
      'amount',
      '-amount',
      'status',
      '-status',
      'created_at',
      '-created_at',
    ])
    .default('due_date'),
})

export type BankTxSearch = z.infer<typeof bankTxSearchSchema>

/** Ordem default do endpoint — usada quando o 3º clique destrava o header. */
export const BANK_TX_ORDEM_DEFAULT = 'due_date' satisfies BankTxSearch['ordenar']

/** Chaves de filtro da tela — o que "limpar filtros" zera e o que vira chip. */
export const BANK_TX_FILTROS = [
  'de',
  'ate',
  'situacao',
] as const satisfies readonly (keyof BankTxSearch)[]

/**
 * Search params → params da API. ESTA FUNÇÃO É A FRONTEIRA: `direction` e
 * `status` vêm da tela (não da URL); daqui saem período e a situação escolhida.
 *
 * `situacao` só entra se for um dos status da IDENTIDADE da tela — narrar
 * "a pagar" (open+overdue+partial) para só "overdue" é válido; pedir "settled"
 * numa tela de aberto é ignorado (cai na identidade), então a tela nunca mente.
 */
export function toBankTxListParams(
  s: BankTxSearch,
  direction: BankTxDirection,
  status: readonly BankTxStatus[],
): BankTxListParams {
  const efetivo = s.situacao !== undefined && status.includes(s.situacao) ? [s.situacao] : status
  return {
    direction,
    status: efetivo,
    ...(s.de !== undefined && { due_after: s.de }),
    ...(s.ate !== undefined && { due_before: s.ate }),
    order_by: s.ordenar,
    ...toLimitOffset(s),
  }
}
